/**
 * Skeleton loading placeholders.
 *
 * - `Skeleton` — the low-level pulse block (shadcn primitive), for ad-hoc
 *   bars/shapes.
 * - `SkeletonCard` — a card of grey placeholder bars (title + body lines) with
 *   a shimmer sweep, so every loading surface reads as one system. The grey
 *   bars are solid (always visible); the sweep is a separate overlay, so it
 *   degrades to a static skeleton if a consumer hasn't defined the shimmer
 *   utility.
 *
 * @see https://ui.shadcn.com/docs/primitives/skeleton
 */
import { cn } from "../../lib/utils"

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("animate-pulse rounded-md bg-muted", className)}
      {...props}
    />
  )
}

/**
 * - `card` — a title bar over full-width body lines (chat history / artefact &
 *   briefing panels).
 * - `row` — a compact list row: a leading icon square beside a title, body
 *   lines and a short meta line (e.g. the homepage Inbox list). Reuse for any
 *   icon-plus-text list that streams in.
 */
export type SkeletonCardVariant = "card" | "row"

interface SkeletonCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: SkeletonCardVariant
  /**
   * Body lines under the title — the "description / details" bars. Defaults to
   * 3 for `card` and 2 for `row`.
   */
  lines?: number
}

/** A solid, clearly-visible grey placeholder shape (theme-aware). */
const BAR = "rounded bg-zinc-200 dark:bg-zinc-800"
/**
 * A soft highlight that sweeps across the whole card — the shimmer. Kept on its
 * own overlay element (not on the bars) so it never cancels their fill colour,
 * and so a missing shimmer utility just leaves a static grey skeleton.
 */
const SHEEN = "pointer-events-none absolute inset-0 bg-shimmer-gradient animate-shimmer"

function SkeletonCard({
  className,
  variant = "card",
  lines,
  ...props
}: SkeletonCardProps) {
  const bodyCount = Math.max(0, lines ?? (variant === "row" ? 2 : 3))
  const bodyLines = Array.from({ length: bodyCount })

  if (variant === "row") {
    return (
      <div
        role="status"
        aria-label="Loading"
        className={cn(
          "relative flex items-start gap-2 overflow-hidden rounded-lg border border-border bg-card px-3 py-2.5",
          className,
        )}
        {...props}
      >
        <div className={cn("mt-0.5 size-[22px] shrink-0", BAR)} />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          {/* title */}
          <div className={cn("h-3.5 w-2/5", BAR)} />
          {/* description / details */}
          {bodyLines.map((_, i) => (
            <div
              key={i}
              className={cn("h-3", i === bodyCount - 1 ? "w-3/5" : "w-full", BAR)}
            />
          ))}
          {/* meta (timestamp) */}
          <div className={cn("h-2.5 w-16", BAR)} />
        </div>
        <div className={SHEEN} />
      </div>
    )
  }

  return (
    <div
      role="status"
      aria-label="Loading"
      className={cn(
        "relative overflow-hidden rounded-lg border border-border bg-card p-6",
        className,
      )}
      {...props}
    >
      {/* title */}
      <div className={cn("h-4 w-1/2", BAR)} />
      {/* body lines */}
      <div className="mt-4 flex flex-col gap-2.5">
        {bodyLines.map((_, i) => (
          <div
            key={i}
            className={cn("h-3", i === bodyCount - 1 ? "w-2/5" : "w-full", BAR)}
          />
        ))}
      </div>
      <div className={SHEEN} />
    </div>
  )
}

export { Skeleton, SkeletonCard }
